import React from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CardActionArea,
  Box,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import { CalendarMonth as CalendarIcon } from "@mui/icons-material";

/**
 * LatestPosts Component
 *
 * Displays a responsive grid of WordPress posts with featured images,
 * titles, excerpts, and metadata.
 *
 * @param {Object} props
 * @param {Array} props.posts - Array of WordPress post objects from REST API
 * @returns {React.ReactElement}
 */
function LatestPosts({ posts = [] }) {
  // Handle empty posts array
  if (!posts || posts.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        No posts found.
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {posts.map((post) => {
        // Extract featured image if available
        const featuredImage =
          post._embedded &&
          post._embedded["wp:featuredmedia"] &&
          post._embedded["wp:featuredmedia"][0]
            ? post._embedded["wp:featuredmedia"][0].source_url
            : null;

        // Format date
        const postDate = new Date(post.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        // Get categories if available
        const categories =
          post._embedded &&
          post._embedded["wp:term"] &&
          post._embedded["wp:term"][0]
            ? post._embedded["wp:term"][0]
            : [];

        return (
          <Grid item xs={12} sm={6} md={4} key={post.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition:
                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea
                component={Link}
                to={`/post/${post.id}`}
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                {featuredImage && (
                  <CardMedia
                    component="img"
                    height="140"
                    image={featuredImage}
                    alt={post.title.rendered || "Post thumbnail"}
                    sx={{ objectFit: "cover" }}
                  />
                )}
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography variant="h6" component="h2" gutterBottom>
                    <span
                      dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                    />
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">{postDate}</Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, flexGrow: 1 }}
                    dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                  />

                  {categories.length > 0 && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {categories.map((category) => (
                          <Chip
                            key={category.id}
                            label={category.name}
                            size="small"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default LatestPosts;
